import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { API, apiFetch } from "@/lib/api";

const money = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ageingDaysOf = (o) => {
  const raw = o.order_date || o.created_at;
  if (!raw) return 0;
  const billDate = new Date(String(raw).slice(0, 10));
  if (isNaN(billDate.getTime())) return 0;
  const today = new Date();
  const diffMs = new Date(today.getFullYear(), today.getMonth(), today.getDate()) - new Date(billDate.getFullYear(), billDate.getMonth(), billDate.getDate());
  return Math.max(0, Math.round(diffMs / 86400000));
};
const ageingColor = (days) => (days > 90 ? "text-red-600" : days > 60 ? "text-orange-600" : days > 30 ? "text-amber-600" : "text-green-600");

const SupplierPayPODialog = ({ open, onOpenChange, supplier, onPaid }) => {
  const [orders, setOrders] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selected, setSelected] = useState({}); // po_id -> { checked, amount }
  const [method, setMethod] = useState("cash");
  const [bankId, setBankId] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [payDate, setPayDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    if (!open || !supplier) return;
    (async () => {
      setLoading(true);
      try {
        const [poRes, bankRes] = await Promise.all([
          apiFetch(`${API}/purchase-orders`),
          apiFetch(`${API}/bank-accounts`),
        ]);
        const pos = await poRes.json();
        const banks = bankRes.ok ? await bankRes.json() : [];
        const list = (Array.isArray(pos) ? pos : []).filter(
          (o) => o.supplier_name === supplier.name && (Number(o.total_amount || 0) - Number(o.paid_amount || 0)) > 0.01 && o.status !== "cancelled"
        );
        setOrders(list);
        setBankAccounts(Array.isArray(banks) ? banks.filter((b) => b.is_active !== false) : []);
        setSelected({});
      } catch (e) {
        toast.error("Failed to load purchase orders");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, supplier]);

  const remainingOf = (o) => Math.round((Number(o.total_amount || 0) - Number(o.paid_amount || 0)) * 100) / 100;

  const toggle = (o) => {
    setSelected((prev) => {
      const cur = prev[o.id];
      if (cur?.checked) return { ...prev, [o.id]: { checked: false, amount: cur.amount } };
      return { ...prev, [o.id]: { checked: true, amount: remainingOf(o) } };
    });
  };
  const setAmount = (o, val) => {
    setSelected((prev) => ({ ...prev, [o.id]: { checked: true, amount: val } }));
  };

  const total = useMemo(
    () => Object.values(selected).reduce((s, x) => (x.checked ? s + (parseFloat(x.amount) || 0) : s), 0),
    [selected]
  );

  const submit = async () => {
    const allocations = orders
      .filter((o) => selected[o.id]?.checked && (parseFloat(selected[o.id].amount) || 0) > 0)
      .map((o) => ({ purchase_order_id: o.id, amount: parseFloat(selected[o.id].amount) }));
    if (allocations.length === 0) {
      toast.error("Select at least one purchase order and enter an amount");
      return;
    }
    if (method === "bank_transfer" && !bankId) {
      toast.error("Select a bank account");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiFetch(`${API}/suppliers/${supplier.id}/pay-purchase-orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          allocations,
          payment_method: method,
          bank_account_id: method === "bank_transfer" ? bankId : null,
          bank_account: method === "bank_transfer" ? (bankAccounts.find((b) => b.id === bankId)?.bank_name || null) : null,
          payment_date: payDate,
          transaction_reference: reference || null,
          notes: notes || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Payment failed");
      toast.success(data.message || "Payment recorded");
      onPaid && onPaid();
      setReceipt({
        supplierName: supplier.name,
        date: payDate,
        method: method === "bank_transfer" ? "Bank Transfer" : "Cash",
        bankName: method === "bank_transfer" ? (() => { const b = bankAccounts.find((x) => x.id === bankId); return b ? `${b.bank_name} - ${b.account_number}` : ""; })() : "",
        reference,
        notes,
        payments: data.payments || [],
        total: data.total_paid != null ? data.total_paid : total,
        printedAt: new Date().toLocaleString("en-IN"),
      });
    } catch (e) {
      toast.error(e.message || "Failed to record payment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (o) => {
    if (!o) setReceipt(null);
    onOpenChange(o);
  };

  const printVoucher = () => {
    if (!receipt) return;
    const rows = receipt.payments.map((p) => `
      <tr>
        <td style="padding:6px 8px;border:1px solid #ddd">${p.po_number}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;text-align:right">${money(p.amount)}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;text-align:right">${money(p.remaining_amount)}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;text-transform:capitalize">${p.payment_status}</td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><title>Payment Voucher</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;color:#111;padding:28px;max-width:720px;margin:auto}
      h1{font-size:20px;margin:0}.muted{color:#666;font-size:12px}
      table{border-collapse:collapse;width:100%;margin-top:14px;font-size:13px}
      th{background:#f4f4f5;padding:6px 8px;border:1px solid #ddd;text-align:left}
      .meta{margin-top:14px;font-size:13px;line-height:1.7}
      .total{margin-top:12px;text-align:right;font-size:16px;font-weight:bold}
      .sign{margin-top:56px;display:flex;justify-content:space-between;font-size:12px;color:#444}</style></head>
      <body>
        <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:10px">
          <div><h1>GARAGE</h1><div class="muted">Management System</div></div>
          <div style="text-align:right"><h2 style="margin:0;font-size:16px">PAYMENT VOUCHER</h2><div class="muted">Printed: ${receipt.printedAt}</div></div>
        </div>
        <div class="meta">
          <div><b>Paid To:</b> ${receipt.supplierName}</div>
          <div><b>Payment Date:</b> ${receipt.date}</div>
          <div><b>Method:</b> ${receipt.method}${receipt.bankName ? " — " + receipt.bankName : ""}</div>
          ${receipt.reference ? `<div><b>Reference:</b> ${receipt.reference}</div>` : ""}
          ${receipt.notes ? `<div><b>Notes:</b> ${receipt.notes}</div>` : ""}
        </div>
        <table>
          <thead><tr><th>Purchase Order</th><th style="text-align:right">Amount Paid</th><th style="text-align:right">Balance</th><th>Status</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="total">Total Paid: ${money(receipt.total)}</div>
        <div class="sign"><span>Received by: ____________________</span><span>Authorised by: ____________________</span></div>
      </body></html>`;
    const w = window.open("", "_blank", "width=800,height=600");
    if (!w) { toast.error("Pop-up blocked. Allow pop-ups to print."); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pay-po-dialog">
        <DialogHeader>
          <DialogTitle>{receipt ? "Payment Voucher" : `Pay Purchase Orders — ${supplier?.name}`}</DialogTitle>
          <DialogDescription>{receipt ? "Payment recorded. Print or save the voucher below." : "Choose the purchase orders to settle. The payment updates each PO, the supplier balance and the cash/bank ledgers."}</DialogDescription>
        </DialogHeader>

        {receipt ? (
          <div className="space-y-4" data-testid="pay-po-receipt">
            <div className="rounded-md border p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Paid To</span><span className="font-semibold">{receipt.supplierName}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Date</span><span>{receipt.date}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Method</span><span>{receipt.method}{receipt.bankName ? ` — ${receipt.bankName}` : ""}</span></div>
              {receipt.reference && <div className="flex justify-between"><span className="text-gray-500">Reference</span><span>{receipt.reference}</span></div>}
            </div>
            <div className="rounded-md border overflow-hidden">
              <div className="grid grid-cols-12 bg-gray-50 text-xs font-semibold text-gray-500 px-3 py-2">
                <span className="col-span-5">Purchase Order</span>
                <span className="col-span-3 text-right">Amount Paid</span>
                <span className="col-span-2 text-right">Balance</span>
                <span className="col-span-2 text-right">Status</span>
              </div>
              {receipt.payments.map((p) => (
                <div key={p.purchase_order_id} className="grid grid-cols-12 px-3 py-2 text-sm border-t">
                  <span className="col-span-5 font-medium">{p.po_number}</span>
                  <span className="col-span-3 text-right">{money(p.amount)}</span>
                  <span className="col-span-2 text-right text-gray-500">{money(p.remaining_amount)}</span>
                  <span className="col-span-2 text-right capitalize">{p.payment_status}</span>
                </div>
              ))}
            </div>
            <div className="text-right text-lg font-bold">Total Paid: <span className="text-emerald-700" data-testid="receipt-total">{money(receipt.total)}</span></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => handleClose(false)} data-testid="receipt-close">Close</Button>
              <Button onClick={printVoucher} data-testid="receipt-print">Print Voucher</Button>
            </div>
          </div>
        ) : loading ? (
          <p className="text-sm text-gray-500 py-6 text-center">Loading purchase orders…</p>
        ) : orders.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center" data-testid="pay-po-empty">No unpaid purchase orders for this supplier.</p>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-12 text-xs font-semibold text-gray-500 px-2">
              <span className="col-span-1"></span>
              <span className="col-span-2">PO Number</span>
              <span className="col-span-2 text-right">Total</span>
              <span className="col-span-1 text-center">Aging</span>
              <span className="col-span-2 text-right">Paid</span>
              <span className="col-span-2 text-right">Remaining</span>
              <span className="col-span-2 text-right">Pay</span>
            </div>
            {orders.map((o) => {
              const rem = remainingOf(o);
              const row = selected[o.id];
              const ageDays = ageingDaysOf(o);
              return (
                <div key={o.id} className="grid grid-cols-12 items-center gap-1 rounded border px-2 py-2" data-testid={`pay-po-row-${o.po_number}`}>
                  <span className="col-span-1">
                    <Checkbox checked={!!row?.checked} onCheckedChange={() => toggle(o)} data-testid={`pay-po-check-${o.po_number}`} />
                  </span>
                  <span className="col-span-2 text-sm font-medium">{o.po_number}</span>
                  <span className="col-span-2 text-right text-sm">{money(o.total_amount)}</span>
                  <span className={`col-span-1 text-center text-sm font-semibold ${ageingColor(ageDays)}`} data-testid={`pay-po-aging-${o.po_number}`}>{ageDays}d</span>
                  <span className="col-span-2 text-right text-sm text-gray-500">{money(o.paid_amount)}</span>
                  <span className="col-span-2 text-right text-sm font-semibold text-amber-700">{money(rem)}</span>
                  <span className="col-span-2">
                    <Input
                      type="number"
                      className="h-8 text-right"
                      disabled={!row?.checked}
                      value={row?.checked ? row.amount : ""}
                      max={rem}
                      onChange={(e) => setAmount(o, e.target.value)}
                      data-testid={`pay-po-amount-${o.po_number}`}
                    />
                  </span>
                </div>
              );
            })}

            <div className="grid grid-cols-2 gap-3 pt-3">
              <div>
                <Label className="text-xs">Payment Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="pay-po-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {method === "bank_transfer" && (
                <div>
                  <Label className="text-xs">Bank Account</Label>
                  <Select value={bankId} onValueChange={setBankId}>
                    <SelectTrigger data-testid="pay-po-bank"><SelectValue placeholder="Select bank" /></SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.bank_name} - {b.account_number} ({money(b.current_balance)})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs">Payment Date</Label>
                <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="pay-po-date" />
              </div>
              <div>
                <Label className="text-xs">Reference (optional)</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Txn / cheque no." data-testid="pay-po-reference" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notes (optional)</Label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="pay-po-notes" />
              </div>
            </div>

            <div className="flex items-center justify-between border-t pt-3 mt-2">
              <span className="text-sm font-semibold">Total to pay: <span className="text-emerald-700" data-testid="pay-po-total">{money(total)}</span></span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pay-po-cancel">Cancel</Button>
                <Button onClick={submit} disabled={submitting || total <= 0} data-testid="pay-po-submit">
                  {submitting ? "Recording…" : "Record Payment"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SupplierPayPODialog;
